import { useEffect, useMemo, useState } from "react";
import { Filter, X } from "lucide-react";
import {
  countConditions,
  defaultFilter,
  type FilterTree,
  type ViewEntityType,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { FilterTreeBuilder } from "./filter-tree-builder";

// Phase 7 sub-bucket 17 — SPA port of components/shared/views/AddFilterMenu.tsx.
// Wraps the FilterTreeBuilder inside a Popover so the active view's filter
// (or an ad-hoc URL filter) can be edited in-place on a list page.
//
// State model:
//   - `value` is the canonical filter that's actually applied on the page
//     (lifted from the URL / active view by the parent).
//   - Local `draft` state buffers edits in the popover so Cancel doesn't
//     leak half-typed changes back into the URL.
//   - On Apply, forwards the draft to the parent's onApply callback —
//     which is responsible for the URL update or saved-view PATCH.

type Props = {
  entityType: ViewEntityType;
  value: FilterTree | null;
  onApply: (next: FilterTree | null) => void;
  /** Externally-controlled open state for parent-driven menu entries. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Subtle differentiator when the URL filter overrides the active view's stored filter. */
  isOverride?: boolean;
  disabled?: boolean;
};

export function AddFilterMenu({
  entityType,
  value,
  onApply,
  open: controlledOpen,
  onOpenChange,
  isOverride = false,
  disabled = false,
}: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const initial = useMemo<FilterTree>(
    () => value ?? defaultFilter(entityType),
    [value, entityType],
  );
  const [draft, setDraft] = useState<FilterTree>(initial);

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const activeConditionCount = countConditions(value);

  function commit() {
    // Treat an "empty group" as `null` (no filter) so the URL doesn't
    // accumulate noisy empty trees.
    const hasAny = countConditions(draft) > 0;
    onApply(hasAny ? draft : null);
    setOpen(false);
  }

  function clear() {
    setDraft(defaultFilter(entityType));
    onApply(null);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            "h-8 gap-1.5 px-2.5 text-xs",
            activeConditionCount > 0 &&
              "border-accent/50 bg-accent/10 text-foreground",
          )}
        >
          <Filter className="h-3.5 w-3.5" />
          Filter
          {activeConditionCount > 0 && (
            <span
              className={cn(
                "ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums",
                isOverride
                  ? "bg-amber-500 text-amber-50"
                  : "bg-accent text-accent-foreground",
              )}
              aria-label={`${activeConditionCount} active conditions`}
            >
              {activeConditionCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[640px] max-w-[calc(100vw-2rem)] p-3"
      >
        <div className="mb-2 flex items-center justify-between">
          <h4 className="font-display text-sm font-semibold tracking-tight">
            Filter
          </h4>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mb-2 text-xs text-muted-foreground">
          Combine conditions with AND / OR to filter the list.
        </p>
        <FilterTreeBuilder
          entityType={entityType}
          value={draft}
          onChange={setDraft}
        />
        <div className="mt-3 flex items-center justify-end gap-2 border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clear}
            disabled={activeConditionCount === 0 && countConditions(draft) === 0}
          >
            Clear
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={commit}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
